import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  api,
  ApiError,
  type Band,
  type MediaCategory,
  type QueueCard,
  type SubmitResult,
  type WordList,
} from '../lib/api.ts';
import { usePreferencesStore } from './preferences.ts';

const ACCEPT_MS = 450;
const MAX_ATTEMPTS = 3;

export type Phase = 'idle' | 'loading' | 'intro' | 'prompt' | 'result' | 'empty' | 'error';

export const useSessionStore = defineStore('session', () => {
  const preferences = usePreferencesStore();

  const queue = ref<QueueCard[]>([]);
  const index = ref(0);
  const phase = ref<Phase>('idle');
  const answer = ref('');
  const result = ref<SubmitResult | null>(null);
  const errorMessage = ref('');

  const listSlug = ref('general');
  const categorySlug = ref<string | undefined>(undefined);

  const lists = ref<WordList[]>([]);
  const categories = ref<MediaCategory[]>([]);
  const bands = ref<Band[]>([]);
  const bandCount = computed(() => bands.value.length);

  const listTitle = computed(
    () => lists.value.find((l) => l.slug === listSlug.value)?.title ?? listSlug.value,
  );
  const categoryTitle = computed(() =>
    categorySlug.value
      ? (categories.value.find((c) => c.slug === categorySlug.value)?.title ?? categorySlug.value)
      : '',
  );

  async function loadCatalog(): Promise<void> {
    const [listsResponse, categoriesResponse, bandsResponse] = await Promise.all([
      api.lists().catch(() => ({ lists: [] })),
      api.categories().catch(() => ({ categories: [] })),
      api.bands().catch(() => ({ bands: [] })),
    ]);
    lists.value = listsResponse.lists;
    categories.value = categoriesResponse.categories;
    bands.value = bandsResponse.bands;

    if (lists.value.length > 0 && !lists.value.some((l) => l.slug === listSlug.value)) {
      listSlug.value = lists.value[0]!.slug;
    }
  }

  const revealedLemma = ref<string | null>(null);
  const revealedMnemonic = ref<string | null>(null);
  const lettersShown = ref(0);
  const hintComplete = ref(false);
  const hintUnlocked = ref(false);
  const hintReadyAt = ref(0);
  const busy = ref(false);
  const cold = ref(false);

  const attemptId = ref<string | undefined>(undefined);
  const attempts = ref(0);
  const shakes = ref(0);

  const accepted = ref(false);
  let acceptTimer: ReturnType<typeof setTimeout> | undefined;

  let hintTimer: ReturnType<typeof setTimeout> | undefined;
  let shownAt = 0;

  const current = computed<QueueCard | null>(() => queue.value[index.value] ?? null);
  const remaining = computed(() => Math.max(0, queue.value.length - index.value));
  const total = computed(() => queue.value.length);
  const position = computed(() => Math.min(index.value + 1, queue.value.length));
  const answeredCount = ref(0);
  const correctCount = ref(0);
  const newWordsCount = ref(0);
  const nextDueAt = ref<string | null>(null);
  const dueTotal = ref(0);
  const newWithheld = ref(false);
  const backlogLimit = ref(0);
  const dueWaiting = computed(() =>
    Math.max(0, dueTotal.value - queue.value.filter((card) => card.kind === 'due').length),
  );

  function resetCardState(): void {
    if (acceptTimer) clearTimeout(acceptTimer);
    accepted.value = false;
    busy.value = false;
    cold.value = false;
    errorMessage.value = '';
    answer.value = '';
    attemptId.value = globalThis.crypto?.randomUUID?.();
    attempts.value = 0;
    result.value = null;
    revealedLemma.value = null;
    revealedMnemonic.value = null;
    lettersShown.value = 0;
    hintComplete.value = false;
    hintUnlocked.value = false;
    hintReadyAt.value = 0;
    if (hintTimer) clearTimeout(hintTimer);
  }

  function armRecall(card: QueueCard): void {
    hintReadyAt.value = Date.now() + card.hintDelayMs;
    hintTimer = setTimeout(() => {
      hintUnlocked.value = true;
    }, card.hintDelayMs);
  }

  function armCard(): void {
    const card = current.value;
    if (!card) {
      phase.value = 'empty';
      return;
    }
    resetCardState();
    shownAt = Date.now();

    if (card.kind === 'new') {
      cold.value = true;
      phase.value = 'prompt';
      return;
    }

    phase.value = 'prompt';
    armRecall(card);
  }

  function showIntro(): void {
    const card = current.value;
    if (!card || phase.value !== 'prompt' || !cold.value) return;
    cold.value = false;
    answer.value = '';
    revealedLemma.value = card.lemma;
    revealedMnemonic.value = card.mnemonic?.text ?? null;
    phase.value = 'intro';
  }

  function beginRecall(): void {
    if (phase.value !== 'intro') return;
    revealedLemma.value = null;
    revealedMnemonic.value = null;
    phase.value = 'prompt';
    shownAt = Date.now();
    const card = current.value;
    if (card) armRecall(card);
  }

  async function load(options?: { list?: string; category?: string }): Promise<void> {
    if (options?.list) listSlug.value = options.list;
    if (options && 'category' in options) categorySlug.value = options.category;

    if (acceptTimer) clearTimeout(acceptTimer);
    accepted.value = false;
    if (phase.value !== 'empty') phase.value = 'loading';
    busy.value = true;
    errorMessage.value = '';
    try {
      const queued = await api.queue({
        list: listSlug.value,
        ...(categorySlug.value ? { category: categorySlug.value } : {}),
      });
      const cards = queued.cards;
      queue.value = cards;
      dueTotal.value = queued.dueTotal;
      newWithheld.value = queued.newWithheld;
      backlogLimit.value = queued.backlogLimit;
      index.value = 0;
      const recheck = cards.length === 0 && !options;
      if (!recheck) {
        answeredCount.value = 0;
        correctCount.value = 0;
        newWordsCount.value = 0;
        nextDueAt.value = null;
      }
      if (cards.length === 0) {
        phase.value = 'empty';
        return;
      }
      armCard();
    } catch (error) {
      phase.value = 'error';
      errorMessage.value =
        error instanceof ApiError ? error.message : 'Не удалось загрузить карточки';
    } finally {
      busy.value = false;
    }
  }

  async function takeLetter(): Promise<void> {
    const card = current.value;
    if (!card || !hintUnlocked.value || hintComplete.value || busy.value) return;
    busy.value = true;
    try {
      const hint = await api.letter(card.senseId, lettersShown.value + 1);
      lettersShown.value = hint.letters.length;
      hintComplete.value = hint.complete;
      answer.value = hint.letters;
    } catch (error) {
      errorMessage.value = error instanceof ApiError ? error.message : 'Подсказка недоступна';
    } finally {
      busy.value = false;
    }
  }

  async function run(giveUp: boolean): Promise<void> {
    const card = current.value;
    if (!card || phase.value !== 'prompt' || busy.value || accepted.value) return;
    if (giveUp ? cold.value : !answer.value.trim()) return;
    busy.value = true;
    errorMessage.value = '';
    try {
      const durationMs = Math.min(3_600_000, Date.now() - shownAt);
      const attempt = attemptId.value ? { attemptId: attemptId.value } : {};
      if (cold.value) {
        const known = await api.known(card.senseId, {
          answer: answer.value,
          durationMs,
          ...attempt,
        });
        if (!known.result) {
          showIntro();
          return;
        }
        result.value = known.result;
      } else {
        if (!giveUp) {
          const { verdict } = await api.check(card.senseId, { answer: answer.value });
          if (verdict === 'incorrect' && attempts.value < MAX_ATTEMPTS - 1) {
            attempts.value += 1;
            shakes.value += 1;
            return;
          }
        }
        const attemptNo = giveUp ? MAX_ATTEMPTS : attempts.value + 1;
        result.value = await api.submit(card.senseId, {
          answer: giveUp ? '' : answer.value,
          lettersShown: lettersShown.value,
          durationMs,
          ...(attemptNo > 1 ? { attempts: attemptNo } : {}),
          ...attempt,
        });
      }
      answeredCount.value += 1;
      if (result.value.verdict === 'correct') correctCount.value += 1;
      if (card.kind === 'new') newWordsCount.value += 1;
      if (!nextDueAt.value || Date.parse(result.value.due) < Date.parse(nextDueAt.value)) {
        nextDueAt.value = result.value.due;
      }
      revealedLemma.value = result.value.lemma;
      if (result.value.verdict === 'correct' && preferences.autoAdvance) {
        accepted.value = true;
        acceptTimer = setTimeout(advance, ACCEPT_MS);
      } else {
        phase.value = 'result';
      }
    } catch (error) {
      errorMessage.value = error instanceof ApiError ? error.message : 'Не удалось сохранить ответ';
    } finally {
      busy.value = false;
    }
  }

  const submit = (): Promise<void> => run(false);
  const giveUp = (): Promise<void> => run(true);

  function next(): void {
    if (phase.value !== 'result') return;
    advance();
  }

  function advance(): void {
    index.value += 1;
    if (index.value >= queue.value.length) {
      phase.value = 'empty';
      resetCardState();
      return;
    }
    armCard();
  }

  return {
    queue,
    index,
    phase,
    answer,
    result,
    errorMessage,
    listSlug,
    categorySlug,
    lists,
    categories,
    bands,
    bandCount,
    listTitle,
    categoryTitle,
    loadCatalog,
    revealedLemma,
    revealedMnemonic,
    accepted,
    lettersShown,
    hintComplete,
    hintUnlocked,
    hintReadyAt,
    busy,
    cold,
    current,
    remaining,
    total,
    position,
    answeredCount,
    correctCount,
    newWordsCount,
    nextDueAt,
    dueTotal,
    newWithheld,
    backlogLimit,
    dueWaiting,
    load,
    showIntro,
    beginRecall,
    takeLetter,
    submit,
    giveUp,
    shakes,
    next,
  };
});
